/**
 * Save helpers for the TikTok workspace. The review step's build log runs
 * them in dependency order (campaign → ad group → ad); each one is a no-op
 * when the entity already exists on TikTok (apiId set), which is what makes
 * a failed publish resumable.
 *
 * Every TikTok-specific rule lives here or in lib/tiktok-matrix.ts — placement
 * dependent identity types, video-only formats, goal derivation, pixel /
 * app / frequency-cap requirements, FOLLOWERS no-CTA, lead forms, minimum
 * budgets. Results carry a user-facing `error` instead of toasting so the
 * caller decides how to surface it.
 */
import { validLandingUrl } from './validation';
import { tiktokService } from '@/services/tiktok.service';
import type { WorkspaceAd, WorkspaceAdGroup, WorkspaceCampaign } from '@/contexts/TiktokWorkspaceContext';
import {
  DEFAULT_FREQUENCY_CAP, adDestinationFor, defaultGoalFor, goalSpec, goalsForObjective, objectiveSpec,
  type TiktokBidType,
} from '@/lib/tiktok-matrix';
import { adForm, errMsg, groupForm, groupPlacementsOf, identityFitsPlacement, type Identity } from './workspaceShared';

type ObjectiveType =
  | 'TRAFFIC' | 'CONVERSIONS' | 'APP_PROMOTION' | 'REACH'
  | 'VIDEO_VIEWS' | 'ENGAGEMENT' | 'LEAD_GENERATION' | 'PRODUCT_SALES';
type BudgetMode = 'BUDGET_MODE_DAY' | 'BUDGET_MODE_TOTAL' | 'BUDGET_MODE_INFINITE';
type AdGroupBudgetMode = 'BUDGET_MODE_DAY' | 'BUDGET_MODE_TOTAL';
// BID_TYPE_MAX_CONVERSION is rejected by TikTok in every combination.
type BidType = TiktokBidType;
type ScheduleType = 'SCHEDULE_FROM_NOW' | 'SCHEDULE_START_END';
type AdFormat = 'SINGLE_VIDEO' | 'SINGLE_IMAGE' | 'CAROUSEL_ADS' | 'COLLECTION_ADS';

export type SaveResult =
  | { ok: true; apiId: string; created: boolean }
  | { ok: false; error: string; fromApi: boolean };

const fail = (error: string): SaveResult => ({ ok: false, error, fromApi: false });

export async function ensureCampaignSaved(
  campaign: WorkspaceCampaign,
  lockCampaign: (apiId: string) => void,
): Promise<SaveResult> {
  if (campaign.apiId) return { ok: true, apiId: campaign.apiId, created: false };

  const cf = campaign.formData as Record<string, string>;
  const name = (cf.campaignName ?? cf.name ?? '').trim();
  const objective = (cf.objectiveType ?? campaign.objective ?? '') as ObjectiveType;
  const budgetMode = (cf.budgetMode ?? 'BUDGET_MODE_DAY') as BudgetMode;
  const budgetStr = cf.budget ?? '';
  const opStatus = (cf.operationStatus ?? 'DISABLE') as 'ENABLE' | 'DISABLE';

  if (!name || !objective) return fail('Add a campaign name + pick an objective first.');
  const spec = objectiveSpec(objective);
  if (spec?.unavailableReason) {
    return fail(`TikTok can't run ${spec.label} campaigns on this advertiser. ${spec.unavailableReason}`);
  }

  const needsBudget = budgetMode !== 'BUDGET_MODE_INFINITE';
  const budgetNum = budgetStr.trim() ? Number(budgetStr) : undefined;
  if (needsBudget && (!budgetNum || !Number.isFinite(budgetNum) || budgetNum <= 0)) {
    return fail('Enter a positive campaign budget.');
  }

  try {
    const r = await tiktokService.createCampaign({
      campaignName: name,
      objectiveType: objective,
      budgetMode,
      budget: needsBudget ? budgetNum : undefined,
      operationStatus: opStatus,
    });
    lockCampaign(r.campaignId);
    return { ok: true, apiId: r.campaignId, created: true };
  } catch (e) {
    return { ok: false, error: errMsg(e, 'TikTok rejected the campaign.'), fromApi: true };
  }
}

export async function ensureAdGroupSaved(
  campaignApiId: string,
  adGroup: WorkspaceAdGroup,
  lockAdGroup: (id: string, apiId: string) => void,
  /**
   * Parent campaign objective. Drives the valid goal set + which extra inputs
   * TikTok demands (see lib/tiktok-matrix.ts).
   */
  objective?: string,
): Promise<SaveResult> {
  if (adGroup.apiId) return { ok: true, apiId: adGroup.apiId, created: false };

  const f = groupForm(adGroup) as Record<string, string | string[]>;
  const adGroupName = ((f.adGroupName as string) ?? (f.name as string) ?? '').trim();
  const placements = groupPlacementsOf(adGroup);
  const optimizationGoal = (f.optimizationGoal as string) || defaultGoalFor(objective);
  const bidType = (((f.bidType as string) === 'BID_TYPE_CUSTOM'
    ? 'BID_TYPE_CUSTOM' : 'BID_TYPE_NO_BID') as BidType);
  const bidPriceStr = (f.bidPrice as string) ?? '';
  const pixelId = (f.pixelId as string) ?? '';
  const optimizationEvent = (f.optimizationEvent as string) ?? '';
  const appId = (f.appId as string) ?? '';
  const frequencyStr = (f.frequency as string) ?? String(DEFAULT_FREQUENCY_CAP.frequency);
  const frequencyScheduleStr = (f.frequencySchedule as string) ?? String(DEFAULT_FREQUENCY_CAP.frequencySchedule);
  const budgetMode = ((f.budgetMode as string) || 'BUDGET_MODE_DAY') as AdGroupBudgetMode;
  const budgetStr = (f.budget as string) ?? '';
  const scheduleType = ((f.scheduleType as string) || 'SCHEDULE_FROM_NOW') as ScheduleType;
  const scheduleStartTime = (f.scheduleStartTime as string) ?? '';
  const scheduleEndTime = (f.scheduleEndTime as string) ?? '';
  const opStatus = ((f.operationStatus as string) || 'DISABLE') as 'ENABLE' | 'DISABLE';
  const locationIds = (Array.isArray(f.locationIds) ? f.locationIds : []) as string[];
  const ageGroups = (Array.isArray(f.ageGroups) ? f.ageGroups : []) as string[];
  const gender = ((f.gender as string) || 'GENDER_UNLIMITED') as 'GENDER_MALE' | 'GENDER_FEMALE' | 'GENDER_UNLIMITED';

  if (!adGroupName) return fail('Ad group name is required.');
  if (placements.length === 0) return fail('Pick at least one placement.');
  if (locationIds.length === 0) return fail('Pick at least one location — TikTok requires geo targeting.');
  if (!objective) return fail('Pick the campaign objective first — it decides which optimisation goals TikTok allows.');

  const budgetNum = Number(budgetStr);
  if (!Number.isFinite(budgetNum) || budgetNum <= 0) return fail('Enter a positive ad group budget.');

  const spec = objectiveSpec(objective);
  const goal = goalSpec(objective, optimizationGoal);
  if (spec?.unavailableReason) {
    return fail(`TikTok can't run ${spec.label} ad groups on this advertiser. ${spec.unavailableReason}`);
  }
  if (spec && !goal) {
    const allowed = goalsForObjective(objective).map((g) => g.label).join(', ') || 'none';
    return fail(`"${optimizationGoal}" isn't a valid optimisation goal for a ${spec.label} campaign. TikTok allows: ${allowed}.`);
  }

  const isCustomBid = bidType === 'BID_TYPE_CUSTOM';
  const manualBidIsConversion = goal?.manualBidField === 'conversion_bid_price';
  const bidPriceNum = isCustomBid ? Number(bidPriceStr) : undefined;
  if (isCustomBid && (!Number.isFinite(bidPriceNum!) || bidPriceNum! <= 0)) {
    return fail(manualBidIsConversion
      ? 'Enter a positive cost per conversion for the ad group\'s manual bid.'
      : 'Enter a positive bid price for the ad group\'s manual bid.');
  }

  const needsPixel = goal?.requires.includes('PIXEL_EVENT') ?? false;
  if (needsPixel && !(pixelId && optimizationEvent)) {
    return fail(`The "${goal?.label}" goal optimises against a pixel event — pick a pixel + optimisation event on the ad group.`);
  }
  const needsApp = goal?.requires.includes('APP') ?? false;
  if (needsApp && !appId) return fail('Pick the app this ad group promotes.');

  const needsFrequency = goal?.requires.includes('FREQUENCY_CAP') ?? false;
  let frequencyNum: number | undefined;
  let frequencyScheduleNum: number | undefined;
  if (needsFrequency) {
    frequencyNum = Number(frequencyStr);
    frequencyScheduleNum = Number(frequencyScheduleStr);
    if (!Number.isFinite(frequencyNum) || frequencyNum < 1) return fail('Frequency cap: show the ad at most 1 or more times.');
    if (!Number.isFinite(frequencyScheduleNum) || frequencyScheduleNum < 1) return fail('Frequency cap: the period must be at least 1 day.');
  }

  if (!scheduleStartTime) return fail('Pick a start time.');
  if (scheduleType === 'SCHEDULE_START_END') {
    if (!scheduleEndTime) return fail('Pick an end time, or switch to "Run continuously".');
    if (new Date(scheduleEndTime) <= new Date(scheduleStartTime)) return fail('End time must be after the start time.');
  }

  try {
    const r = await tiktokService.createAdGroup({
      tiktokCampaignId: campaignApiId,
      adGroupName,
      placements,
      optimizationGoal,
      bidType,
      // The matrix decides which price field TikTok reads for this goal.
      bidPrice: manualBidIsConversion ? undefined : bidPriceNum,
      conversionBidPrice: manualBidIsConversion ? bidPriceNum : undefined,
      pixelId: needsPixel ? pixelId : undefined,
      optimizationEvent: needsPixel ? optimizationEvent : undefined,
      frequency: frequencyNum,
      frequencySchedule: frequencyScheduleNum,
      appId: needsApp ? appId : undefined,
      budgetMode,
      budget: budgetNum,
      scheduleType,
      scheduleStartTime: new Date(scheduleStartTime).toISOString(),
      scheduleEndTime: scheduleType === 'SCHEDULE_START_END' ? new Date(scheduleEndTime).toISOString() : undefined,
      operationStatus: opStatus,
      locationIds,
      ageGroups: ageGroups.length > 0 ? ageGroups : undefined,
      gender: gender !== 'GENDER_UNLIMITED' ? gender : undefined,
    });
    lockAdGroup(adGroup.id, r.adGroupId);
    return { ok: true, apiId: r.adGroupId, created: true };
  } catch (e) {
    return { ok: false, error: errMsg(e, 'TikTok rejected the ad group.'), fromApi: true };
  }
}

/** Minimum specs TikTok enforces on ad videos — below them a video never finishes processing. */
export function videoSpecProblem(s: { width?: number; height?: number; bitRateKbps?: number }): string | null {
  const w = s.width; const h = s.height;
  if (w && h) {
    const meetsMin = (h >= w && w >= 540 && h >= 960) || (w > h && w >= 960 && h >= 540) || (w === h && w >= 640);
    if (!meetsMin) return `${w}×${h}px — TikTok needs at least 540×960 (vertical) or 960×540 (landscape). Re-export at higher resolution.`;
    const ratio = w / h;
    const okRatio = [9 / 16, 1, 16 / 9].some((r) => Math.abs(ratio - r) / r < 0.04);
    if (!okRatio) return `${w}×${h}px is a ${ratio.toFixed(2)}:1 shape — TikTok only accepts 9:16 vertical, 1:1 square, or 16:9 landscape. Re-export (e.g. 1080×1920).`;
  }
  if (s.bitRateKbps && s.bitRateKbps < 516) return `bitrate ${s.bitRateKbps} kbps — TikTok needs at least 516 kbps. Re-export at higher quality.`;
  return null;
}

export async function ensureAdSaved(
  adGroupApiId: string,
  adGroup: WorkspaceAdGroup,
  ad: WorkspaceAd,
  identities: Identity[],
  lockAd: (adGroupId: string, adId: string, apiId: string) => void,
  objective?: string,
): Promise<SaveResult> {
  if (ad.apiId) return { ok: true, apiId: ad.apiId, created: false };

  const f = adForm(ad);
  const g = groupForm(adGroup);
  const adName = ((f.adName as string) ?? '').trim();
  const adText = ((f.adText as string) ?? '').trim();
  const placements = groupPlacementsOf(adGroup);
  const tiktokPlacement = placements.includes('PLACEMENT_TIKTOK');
  const adFormat = ((f.adFormat as string) ?? 'SINGLE_VIDEO') as AdFormat;
  const callToAction = (f.callToAction as string) ?? 'LEARN_MORE';
  const landingPageUrl = ((f.landingPageUrl as string) ?? '').trim();
  const identityId = (f.identityId as string) ?? '';
  const resolvedImageId = (f.resolvedImageId as string) ?? '';
  const resolvedVideoId = (f.resolvedVideoId as string) ?? '';
  const carouselImageIds = Array.isArray(f.carouselImageIds) ? (f.carouselImageIds as string[]) : [];
  const pageId = (f.pageId as string) ?? '';
  const groupGoal = (g.optimizationGoal as string) || defaultGoalFor(objective);
  const destination = adDestinationFor(objective, groupGoal);

  if (!adName) return fail('Ad name is required.');
  if (!adText) return fail('Ad text is required.');
  if (adText.length > 100) return fail('Ad text must be 100 characters or less.');
  if (destination === 'required' && !validLandingUrl(landingPageUrl)) return fail('Enter a valid website URL starting with https:// or http://.');
  if (!identityId) return fail('Pick a publishing identity.');
  const identity = identities.find((i) => i.identityId === identityId);
  if (!identity) return fail('This TikTok identity is no longer available. Refresh and choose a linked account.');
  if (identity && !identityFitsPlacement(identity.identityType, tiktokPlacement)) {
    return fail(tiktokPlacement
      ? 'This identity is a custom identity — TikTok rejects those for in-feed ads. Pick an authorised TikTok account.'
      : 'This identity is a TikTok account — Pangle / Global App Bundle need a custom identity instead.');
  }
  if (destination === 'form' && !pageId) {
    return fail('Lead-generation ads must open an Instant Form — pick one. Create it in TikTok Ads Manager → Assets → Instant Forms if the list is empty.');
  }
  if (adFormat === 'SINGLE_IMAGE' && tiktokPlacement) {
    return fail('Single-image ads don\'t serve on the TikTok placement — use video, or switch the ad group to Pangle/Global App Bundle.');
  }
  if (adFormat === 'SINGLE_IMAGE' && !resolvedImageId) return fail('Upload or pick an image first.');
  if (adFormat === 'SINGLE_VIDEO' && !resolvedVideoId) return fail('Upload or pick a video first.');
  if (adFormat === 'CAROUSEL_ADS' && carouselImageIds.length < 2) return fail('Carousel needs at least 2 images.');
  if (adFormat === 'COLLECTION_ADS') return fail('Collection ads need a catalog — not supported yet.');

  // TikTok transcodes asynchronously; an ad saved against a still-processing
  // video fails on its low-res placeholder cover ("Unsupported image size").
  // Re-check right before the create so a collapsed card's stale gate can't
  // let one through.
  if (adFormat === 'SINGLE_VIDEO') {
    try {
      const s = await tiktokService.videoStatus(resolvedVideoId);
      const problem = videoSpecProblem(s);
      if (problem) return fail(`This video can't be used: ${problem}`);
      if (s.status !== 'PROCESS_OK') {
        return fail('The video is still processing on TikTok — usually under a minute. Retry publish once it\'s ready.');
      }
    } catch (e) {
      return { ok: false, error: errMsg(e, 'Could not check the video\'s status on TikTok.'), fromApi: true };
    }
  }

  let imageIds: string[] | undefined;
  if (adFormat === 'CAROUSEL_ADS') imageIds = carouselImageIds;
  else if (adFormat !== 'SINGLE_VIDEO' && resolvedImageId) imageIds = [resolvedImageId];
  // SINGLE_VIDEO: the backend derives (and caches) the cover image from the
  // video's own frame, and reuses it on a duplicate-name collision.

  try {
    const r = await tiktokService.createAd({
      tiktokAdGroupId: adGroupApiId,
      adName,
      adFormat,
      adText,
      // FOLLOWERS ads reject any CTA; app / form destinations carry no landing page.
      callToAction: destination === 'forbidden' ? undefined : callToAction,
      landingPageUrl: destination === 'required' && landingPageUrl ? landingPageUrl : undefined,
      videoId: adFormat === 'SINGLE_VIDEO' ? resolvedVideoId : undefined,
      imageIds,
      identityId,
      identityType: identity?.identityType,
      pageId: destination === 'form' ? pageId : undefined,
    });
    lockAd(adGroup.id, ad.id, r.adId);
    return { ok: true, apiId: r.adId, created: true };
  } catch (e) {
    return { ok: false, error: errMsg(e, 'TikTok rejected the ad.'), fromApi: true };
  }
}
